/**
 * Shared two-ring loading spinner — the one loading indicator for every app
 * (BL-1124, ratifying the `Spinner` row of the ADR 030 Canon).
 *
 * The merge takes admin-web `LoadingSpinner`'s API (named `size` tokens + the
 * `LoadingState` composite `ui-standards.md` mandates + the `label` prop) and
 * bidder-web `Spinner`'s rendering (two independently-colorable rings). Per
 * ADR 030 Decision 3, "canonical base names the better starting point for the
 * merge, not the look that survives" — so the base is admin's and the look is
 * bidder's, and both halves of the Canon row are honored.
 *
 * WHY two rings rather than a single arc: the outer track ring and the inner
 * counter-rotating accent ring are separately colorable, so one component reads
 * correctly on a white card, a brand-filled button, and a dark surface. A
 * single-color arc (or a bare lucide `Loader2`) has no track, so on a same-hue
 * surface it disappears unless every call site remembers to re-tint it — which
 * is exactly how admin-web accumulated 91 hand-tinted spinners.
 *
 * The rings themselves are CSS: `.ag-spinner` and its ::before/::after
 * pseudo-elements live in `@auctogon/ui/styles/spinner.css`, which each app
 * imports once from its `globals.css`. This component only sizes the box and
 * sets the four custom properties that stylesheet reads.
 */
'use client';

import clsx from 'clsx';
import type { CSSProperties } from 'react';

/**
 * Named size tokens → pixel diameters.
 *
 * The values match admin-web `LoadingSpinner`'s existing Tailwind sizes
 * (`h-3.5`/`h-5`/`h-8`/`h-12`), so its ~43 call sites migrate without any
 * visual change or arithmetic.
 */
export const SPINNER_SIZE_PX = { xs: 14, sm: 20, md: 32, lg: 48 } as const;

/** A named size token. */
export type SpinnerSizeToken = keyof typeof SPINNER_SIZE_PX;

/**
 * Props for {@link Spinner}.
 */
export interface SpinnerProps {
    /**
     * Named token or an explicit pixel diameter. Defaults to `'sm'` (20px).
     *
     * The numeric branch exists because call sites legitimately need diameters
     * the 4-step scale doesn't express (bidder-web uses 12/16/18/24 alongside
     * 20/32). Prefer a token in new code.
     */
    size?: SpinnerSizeToken | number;
    /** Ring thickness in px. Defaults to ~10% of the diameter, floored at 2. */
    strokeWidth?: number;
    /** Outer ring color — the track that keeps the spinner legible on a same-hue surface. */
    trackColor?: string;
    /**
     * Inner (counter-rotating) ring color.
     *
     * Defaults to `currentColor` so the spinner inherits its context's text
     * color. That default is load-bearing, not cosmetic: it is what lets
     * {@link LoadingState} tint via the `text-primary` token and a button tint
     * via its own text color, instead of every call site naming a brand hex.
     */
    accentColor?: string;
    /** Accessible label announced by screen readers. Defaults to `'Loading'`. */
    label?: string;
    /**
     * Render the spinner as PURELY DECORATIVE — `aria-hidden`, no `role`, no
     * label. Use this and only this when the spinner sits inside a control that
     * ALREADY announces the busy state itself (a `<button>` whose text flips to
     * "Saving…", a region with `aria-busy`).
     *
     * Why it exists: an `aria-label` on a descendant is concatenated into its
     * ancestor's accessible name. A default spinner inside `<button>Saving…`
     * makes the button announce "Loading Saving…" — the redundant word wins the
     * first position, and any name-anchored query or voice command that targets
     * "Save" stops matching. Marking the spinner decorative and putting
     * `aria-busy` on the control is the correct division: the control owns the
     * name AND the state; the spinner is the picture of it.
     *
     * Standalone loading surfaces (page placeholders, panels, `LoadingState`)
     * must NOT set this — they are the only thing announcing, so they keep
     * `role="status"` + the label.
     */
    decorative?: boolean;
    /** Additional classes, merged after the base class. */
    className?: string;
}

/**
 * Animated loading spinner with two independently-colored rings.
 *
 * @param props - see {@link SpinnerProps}
 * @returns An accessible spinning indicator (`role="status"`).
 */
export function Spinner({
    size = 'sm',
    strokeWidth,
    trackColor = '#d4d4d8',
    accentColor = 'currentColor',
    label = 'Loading',
    decorative = false,
    className,
}: SpinnerProps) {
    const px = typeof size === 'number' ? size : SPINNER_SIZE_PX[size];
    // Floored at 2px: a raw 10% of the xs size rounds to 1px, which renders as
    // a hairline that all but disappears when a retina display downscales it.
    const bw = strokeWidth ?? Math.max(2, Math.round(px * 0.1));

    return (
        <span
            // Exactly one of these two shapes, never a mix: an announcing
            // status node, or an invisible-to-AT decoration. A `role="status"`
            // that also carried `aria-hidden` would be neither.
            {...(decorative ? { 'aria-hidden': true } : { role: 'status', 'aria-label': label })}
            className={clsx('ag-spinner', className)}
            style={
                {
                    width: px,
                    height: px,
                    '--ag-spinner-bw': `${bw}px`,
                    '--ag-spinner-track': trackColor,
                    '--ag-spinner-accent': accentColor,
                } as CSSProperties
            }
        />
    );
}

/**
 * Props for {@link LoadingState}.
 */
export interface LoadingStateProps {
    /**
     * Additional container classes — typically a height override.
     *
     * Composition is `clsx`, which concatenates rather than resolving
     * conflicting Tailwind utilities, so a height passed here lands *alongside*
     * the default `h-64` rather than replacing it. Converting this to `twMerge`
     * is tracked as BL-1004.
     */
    className?: string;
}

/**
 * Centered loading spinner for use as a page or section placeholder.
 *
 * `text-primary` on the container is what resolves the spinner's
 * `currentColor` accent to the brand green — which is how the brand color
 * reaches the spinner without any call site (or this file) naming a hex, and
 * therefore without tripping `brandhex/no-hardcoded-brand-hex`.
 *
 * @param props - see {@link LoadingStateProps}
 * @returns A centered spinner in a fixed-height flex container.
 */
export function LoadingState({ className }: LoadingStateProps) {
    return (
        <div className={clsx('flex items-center justify-center h-64 text-primary', className)}>
            <Spinner size="md" />
        </div>
    );
}

export default LoadingState;
